import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Loader2, MailCheck } from "lucide-react";

/**
 * 🆕 22/07/2026 — OTP Gate per proteggere l'accesso a schermate ad alta sensibilità.
 * Genera un grant temporaneo (15 min) memorizzato in sessionStorage.
 *
 * Props:
 *  - purpose: string chiave (es. "payments_config")
 *  - requestPath: endpoint POST che invia il codice OTP via email
 *  - verifyPath: endpoint POST che verifica il codice e rilascia il grant
 *  - title, description: testi UI
 *  - children: contenuto renderizzato SOLO dopo verifica riuscita
 */
export function OtpGate({ purpose, requestPath, verifyPath, title, description, children }) {
  const storageKey = `otp_grant_${purpose}`;
  const readGrant = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj?.expires_at) return null;
      if (new Date(obj.expires_at).getTime() < Date.now()) {
        sessionStorage.removeItem(storageKey);
        return null;
      }
      return obj;
    } catch { return null; }
  };
  const [grant, setGrant] = useState(readGrant());
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [email, setEmail] = useState("");

  const requestOtp = async () => {
    setSending(true);
    try {
      const { data } = await api.post(requestPath);
      setEmail(data?.email || "");
      setOtpRequested(true);
      toast.success(`Codice inviato a ${data?.email || "email admin"}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!code || code.length < 6) {
      toast.error("Inserisci il codice a 6 cifre.");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post(verifyPath, { code });
      const newGrant = { expires_at: data.grant_expires_at, purpose };
      sessionStorage.setItem(storageKey, JSON.stringify(newGrant));
      setGrant(newGrant);
      toast.success(`Accesso autorizzato · valido ${data.valid_for_minutes || 15} minuti`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const revoke = () => {
    sessionStorage.removeItem(storageKey);
    setGrant(null);
    setOtpRequested(false);
    setCode("");
    setEmail("");
    toast.info("Sessione revocata. Sarà richiesto un nuovo OTP.");
  };

  if (grant) {
    const remainingMs = new Date(grant.expires_at).getTime() - Date.now();
    const remainingMin = Math.max(0, Math.round(remainingMs / 60000));
    return (
      <div className="relative">
        <div
          data-testid="otp-gate-active"
          className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 mb-3 flex items-center justify-between text-xs"
        >
          <span className="flex items-center gap-2 text-emerald-800">
            <ShieldCheck className="h-3.5 w-3.5" />
            <strong>Sessione OTP attiva</strong> · scade tra ~{remainingMin} min
          </span>
          <button
            data-testid="otp-gate-revoke"
            onClick={revoke}
            className="text-emerald-700 hover:text-emerald-900 underline text-[11px]"
          >
            Blocca ora
          </button>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-10 bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="otp-gate">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-12 w-12 rounded-md bg-indigo-600 text-white flex items-center justify-center">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900">{title || "Accesso protetto"}</h2>
          <p className="text-xs text-slate-500">{description || "Sezione ad alta sensibilità · richiesta verifica OTP."}</p>
        </div>
      </div>

      {!otpRequested ? (
        <>
          <p className="text-sm text-slate-600 mb-4">
            Per motivi di sicurezza, l&apos;accesso a questa sezione richiede una verifica OTP.
            Riceverai un codice a 6 cifre sulla tua email <strong>master</strong> (o email di login).
          </p>
          <Button
            data-testid="otp-gate-request-btn"
            onClick={requestOtp}
            disabled={sending}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MailCheck className="h-4 w-4 mr-2" />}
            Invia codice OTP
          </Button>
        </>
      ) : (
        <>
          <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 mb-4 text-xs text-indigo-800">
            📧 Codice inviato a <strong>{email}</strong>. Il codice scade in 5 minuti.
          </div>
          <Label htmlFor="otp-code" className="text-xs text-slate-600">Codice OTP (6 cifre)</Label>
          <Input
            id="otp-code"
            data-testid="otp-gate-code-input"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            autoFocus
            className="font-mono text-center text-2xl tracking-[0.4em] mt-1 mb-3"
            onKeyDown={(e) => { if (e.key === "Enter") verifyOtp(); }}
          />
          <div className="flex gap-2">
            <Button
              data-testid="otp-gate-verify-btn"
              onClick={verifyOtp}
              disabled={verifying || code.length < 6}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {verifying ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Verifica e accedi
            </Button>
            <Button
              variant="outline"
              onClick={requestOtp}
              disabled={sending}
              data-testid="otp-gate-resend-btn"
            >
              Reinvia
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
